/**
 * SpeakerBindingWizard Component
 *
 * Modal wizard for confirming speaker identities in a transcript.
 * Displays representative audio clips for each speaker and allows users
 * to identify who each speaker is from their list of conversation partners.
 * Uses conversation partner info from the reflection to suggest the likely partner.
 */

import { useState, useEffect, useMemo } from "react";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SpeakerSnippetPlayer } from "./SpeakerSnippetPlayer";
import { SpeakerIdentitySelector } from "./SpeakerIdentitySelector";
import {
  useBindingSnippets,
  useConfirmBinding,
  useConfirmAndAnalyze,
} from "@/hooks/useSpeakerBinding";
import {
  useConversationPartners,
  useCreateConversationPartner,
} from "@/hooks/useConversationPartners";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import type {
  SpeakerIdentity,
  OtherSpeakerBinding,
} from "@/types/speaker-binding";
import type { SpeakerResponse } from "@/types/transcript";

interface SpeakerBindingWizardProps {
  transcriptId: string;
  recordingId?: string;
  /** Pass reflectionId to invalidate feedback queries when speaker names change */
  reflectionId?: string;
  recordingAudioSrc: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Pass speakers to pre-populate wizard with previously confirmed identities */
  speakers?: SpeakerResponse[];
  /** If true, triggers analysis pipeline after confirmation (for initial confirmation flow) */
  triggerAnalysis?: boolean;
  /** Conversation partner ID from the reflection (who was selected when creating) */
  conversationPartnerId?: string | null;
  /** Whether the reflection was created with primary partner selected */
  isWithPrimaryPartner?: boolean;
}

interface WizardSelection {
  identity: SpeakerIdentity;
  partnerId: string | null;
}

export function SpeakerBindingWizard({
  transcriptId,
  recordingId,
  reflectionId,
  recordingAudioSrc,
  isOpen,
  onClose,
  onSuccess,
  speakers,
  triggerAnalysis = false,
  conversationPartnerId,
  isWithPrimaryPartner = false,
}: SpeakerBindingWizardProps) {
  // Fetch current user for their name
  const { data: currentUser } = useCurrentUser();

  // Fetch conversation partners
  const { data: partnersData, isLoading: partnersLoading } =
    useConversationPartners();
  const conversationPartners = useMemo(
    () => partnersData?.partners ?? [],
    [partnersData?.partners]
  );

  // Create conversation partner mutation
  const createPartnerMutation = useCreateConversationPartner();
  const handleCreatePartner = async (
    data: Parameters<typeof createPartnerMutation.mutateAsync>[0]
  ) => {
    return createPartnerMutation.mutateAsync(data);
  };
  const isPartnerLimitReached = conversationPartners.length >= 10;

  // Fetch snippets
  const {
    data: snippetsData,
    isLoading: snippetsLoading,
    error: snippetsError,
  } = useBindingSnippets(transcriptId, { enabled: isOpen });

  // Confirmation mutations - use confirmAndAnalyze when analysis should be triggered
  const confirmBindingMutation = useConfirmBinding(
    transcriptId,
    recordingId,
    reflectionId
  );
  const confirmAndAnalyzeMutation = useConfirmAndAnalyze(
    transcriptId,
    recordingId,
    reflectionId
  );

  // Use the appropriate mutation based on triggerAnalysis prop
  const confirmMutation = triggerAnalysis
    ? confirmAndAnalyzeMutation
    : confirmBindingMutation;

  // Wizard state - track identity and partner ID for each speaker
  const [selectionA, setSelectionA] = useState<WizardSelection>({
    identity: null,
    partnerId: null,
  });
  const [selectionB, setSelectionB] = useState<WizardSelection>({
    identity: null,
    partnerId: null,
  });
  const [otherSelections, setOtherSelections] = useState<
    Record<string, WizardSelection>
  >({});
  const [currentSnippetIndex, setCurrentSnippetIndex] = useState({
    partner_a: 0,
    partner_b: 0,
  });

  // Derive other speaker keys from snippets data (e.g., "other_0", "other_1")
  const otherSpeakerKeys = useMemo(() => {
    if (!snippetsData?.snippets) return [];
    return Object.keys(snippetsData.snippets)
      .filter((key) => key.startsWith("other_"))
      .sort();
  }, [snippetsData?.snippets]);

  // Initialize wizard state when dialog opens - pre-populate if speakers already confirmed
  useEffect(() => {
    if (isOpen) {
      // Check if we have previously confirmed speakers to pre-populate
      const partnerASpeaker = speakers?.find(
        (s) => s.canonical_label === "partner_a"
      );
      const partnerBSpeaker = speakers?.find(
        (s) => s.canonical_label === "partner_b"
      );

      // Determine identity and partner ID based on speaker data
      const getSelection = (
        speaker: SpeakerResponse | undefined
      ): WizardSelection => {
        if (!speaker || !speaker.confirmed_by_user) {
          return { identity: null, partnerId: null };
        }
        if (speaker.bound_user_id) {
          return { identity: "user", partnerId: null };
        }
        if (speaker.bound_partner_name) {
          // Find the partner by name
          const partner = conversationPartners.find(
            (p) => p.name === speaker.bound_partner_name
          );
          return { identity: "partner", partnerId: partner?.id ?? null };
        }
        // Legacy check
        if (
          speaker.custom_name === "You" ||
          (currentUser?.first_name &&
            speaker.custom_name === currentUser.first_name)
        ) {
          return { identity: "user", partnerId: null };
        }
        if (speaker.custom_name && speaker.custom_name !== "Other") {
          const partner = conversationPartners.find(
            (p) => p.name === speaker.custom_name
          );
          return { identity: "partner", partnerId: partner?.id ?? null };
        }
        return { identity: null, partnerId: null };
      };

      setSelectionA(getSelection(partnerASpeaker));
      setSelectionB(getSelection(partnerBSpeaker));
      setOtherSelections({});
      setCurrentSnippetIndex({ partner_a: 0, partner_b: 0 });
    }
  }, [isOpen, speakers, currentUser, conversationPartners]);

  // Get partner by ID helper
  const getPartnerById = (id: string | null) =>
    id ? conversationPartners.find((p) => p.id === id) : null;

  // Confirm handler
  const handleConfirm = async () => {
    // Get partner data for selected partners
    const partnerAData = getPartnerById(selectionA.partnerId);
    const partnerBData = getPartnerById(selectionB.partnerId);

    // Get the user's name from their profile
    const userName = currentUser?.first_name || "User";

    // Determine names based on selected identities
    const partnerAName =
      selectionA.identity === "partner"
        ? partnerAData?.name || "Partner"
        : selectionA.identity === "user"
          ? userName
          : "Other";

    const partnerBName =
      selectionB.identity === "partner"
        ? partnerBData?.name || "Partner"
        : selectionB.identity === "user"
          ? userName
          : "Other";

    // Get pronouns from partner data
    const partnerAPronouns =
      selectionA.identity === "partner" ? partnerAData?.pronouns : undefined;
    const partnerBPronouns =
      selectionB.identity === "partner" ? partnerBData?.pronouns : undefined;

    // Build other_speakers array for N additional speakers
    const otherSpeakersPayload: OtherSpeakerBinding[] = otherSpeakerKeys
      .map((key) => {
        const sel = otherSelections[key];
        if (!sel?.identity) return null;
        const index = parseInt(key.replace("other_", ""), 10);
        const partnerData = getPartnerById(sel.partnerId);
        const name =
          sel.identity === "partner"
            ? partnerData?.name || "Other"
            : sel.identity === "user"
              ? userName
              : "Other";
        const pronouns =
          sel.identity === "partner" ? partnerData?.pronouns : undefined;
        return {
          speaker_index: index,
          name,
          pronouns: pronouns ?? undefined,
          identity: sel.identity ?? undefined,
        };
      })
      .filter(
        (x): x is NonNullable<typeof x> => x !== null
      ) as OtherSpeakerBinding[];

    try {
      await confirmMutation.mutateAsync({
        partner_a_name: partnerAName,
        partner_b_name: partnerBName,
        partner_a_identity: selectionA.identity,
        partner_b_identity: selectionB.identity,
        partner_a_pronouns: partnerAPronouns ?? undefined,
        partner_b_pronouns: partnerBPronouns ?? undefined,
        other_speakers:
          otherSpeakersPayload.length > 0 ? otherSpeakersPayload : undefined,
        confirmed_by_user: true,
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Failed to confirm speaker binding:", error);
    }
  };

  // Check if we can confirm (both speakers identified)
  const canConfirm =
    selectionA.identity !== null && selectionB.identity !== null;

  const isLoading = snippetsLoading || partnersLoading;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Identify Speakers</DialogTitle>
          <DialogDescription>
            Listen to the audio clips below and identify who each speaker is.
            This helps us provide more personalized feedback.
          </DialogDescription>
        </DialogHeader>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-3 text-muted-foreground">Loading...</span>
          </div>
        )}

        {/* Error state */}
        {snippetsError && (
          <div className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-900 dark:text-red-100">
                  Failed to load audio clips
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {snippetsError.message || "An unknown error occurred"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Success state */}
        {snippetsData && !isLoading && (
          <div className="space-y-6">
            {/* Partner A */}
            {snippetsData.snippets.partner_a &&
              snippetsData.snippets.partner_a.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-foreground">
                    Speaker A
                  </h3>

                  {/* Audio snippet */}
                  <SpeakerSnippetPlayer
                    snippet={
                      snippetsData.snippets.partner_a[
                        currentSnippetIndex.partner_a
                      ]
                    }
                    audioSrc={recordingAudioSrc}
                    label="Sample clip"
                  />

                  {/* Identity selector */}
                  <SpeakerIdentitySelector
                    speakerLabel="Speaker A"
                    selectedIdentity={selectionA.identity}
                    onIdentityChange={(identity) =>
                      setSelectionA((prev) => ({ ...prev, identity }))
                    }
                    selectedPartnerId={selectionA.partnerId}
                    onPartnerIdChange={(partnerId) =>
                      setSelectionA((prev) => ({ ...prev, partnerId }))
                    }
                    conversationPartners={conversationPartners}
                    suggestedPartnerId={conversationPartnerId}
                    isWithPrimaryPartner={isWithPrimaryPartner}
                    onCreatePartner={handleCreatePartner}
                    isPartnerLimitReached={isPartnerLimitReached}
                  />
                </div>
              )}

            {/* Partner B */}
            {snippetsData.snippets.partner_b &&
              snippetsData.snippets.partner_b.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-foreground">
                    Speaker B
                  </h3>

                  {/* Audio snippet */}
                  <SpeakerSnippetPlayer
                    snippet={
                      snippetsData.snippets.partner_b[
                        currentSnippetIndex.partner_b
                      ]
                    }
                    audioSrc={recordingAudioSrc}
                    label="Sample clip"
                  />

                  {/* Identity selector */}
                  <SpeakerIdentitySelector
                    speakerLabel="Speaker B"
                    selectedIdentity={selectionB.identity}
                    onIdentityChange={(identity) =>
                      setSelectionB((prev) => ({ ...prev, identity }))
                    }
                    selectedPartnerId={selectionB.partnerId}
                    onPartnerIdChange={(partnerId) =>
                      setSelectionB((prev) => ({ ...prev, partnerId }))
                    }
                    conversationPartners={conversationPartners}
                    suggestedPartnerId={conversationPartnerId}
                    isWithPrimaryPartner={isWithPrimaryPartner}
                    onCreatePartner={handleCreatePartner}
                    isPartnerLimitReached={isPartnerLimitReached}
                  />
                </div>
              )}

            {/* Additional speakers (if present) - optional identification */}
            {otherSpeakerKeys.map((key, idx) => {
              const snippets = snippetsData.snippets[key];
              if (!snippets || snippets.length === 0) return null;
              const sel = otherSelections[key] ?? {
                identity: null,
                partnerId: null,
              };
              const label =
                otherSpeakerKeys.length === 1
                  ? "Additional Speaker"
                  : `Additional Speaker ${idx + 1}`;
              return (
                <div key={key} className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-foreground">
                      {label}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Optional — not required to continue
                    </p>
                  </div>

                  {/* Audio snippet */}
                  <SpeakerSnippetPlayer
                    snippet={snippets[0]}
                    audioSrc={recordingAudioSrc}
                    label="Sample clip"
                  />

                  {/* Identity selector */}
                  <SpeakerIdentitySelector
                    speakerLabel={label}
                    selectedIdentity={sel.identity}
                    onIdentityChange={(identity) =>
                      setOtherSelections((prev) => ({
                        ...prev,
                        [key]: {
                          ...prev[key],
                          identity,
                          partnerId: prev[key]?.partnerId ?? null,
                        },
                      }))
                    }
                    selectedPartnerId={sel.partnerId}
                    onPartnerIdChange={(partnerId) =>
                      setOtherSelections((prev) => ({
                        ...prev,
                        [key]: {
                          ...prev[key],
                          partnerId,
                          identity: prev[key]?.identity ?? null,
                        },
                      }))
                    }
                    conversationPartners={conversationPartners}
                    onCreatePartner={handleCreatePartner}
                    isPartnerLimitReached={isPartnerLimitReached}
                  />
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={confirmMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || confirmMutation.isPending}
          >
            {confirmMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Confirming...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirm Speakers
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
